import { useState, cloneElement } from "react";
import { Tooltip, TooltipRefProps } from "react-tooltip";
import { useVuiContext } from "../context/Context";
import { VuiPortal } from "../portal/Portal";
import { VuiText } from "../typography/Text";
import { VuiTextColor } from "../typography/TextColor";

export type Props = {
  children: React.ReactNode;
  tip: React.ReactNode;
  darkTheme?: boolean;
  position?: TooltipRefProps["place"];
  usePortal?: boolean;
  id?: string;
};

const generateTooltipId = () => {
  return `tooltip-${Math.random().toString(36).slice(2, 9)}`;
};

// Naturally focusable elements that don't need tabIndex.
const FOCUSABLE_ELEMENTS = ["a", "button", "input", "select", "textarea"];

// Determine if the element needs tabIndex to be keyboard accessible.
const needsTabIndex = (element: React.ReactNode): boolean => {
  if (!element || typeof element !== "object" || !("type" in element)) {
    return false;
  }

  const child = element as React.ReactElement;
  const elementType = typeof child.type === "string" ? child.type.toLowerCase() : "";

  // Don't add tabIndex if element is naturally focusable.
  if (FOCUSABLE_ELEMENTS.includes(elementType)) {
    return false;
  }

  // Don't add tabIndex if it already has one.
  if (child.props?.tabIndex !== undefined) {
    return false;
  }

  return true;
};

export const VuiTooltip = ({ children, darkTheme, position, tip, usePortal, ...rest }: Props) => {
  const { getThemeStyle } = useVuiContext();
  const [tooltipId] = useState(generateTooltipId());

  const target = cloneElement(children as React.ReactElement, {
    "data-tooltip-id": tooltipId,
    // Make non-focusable elements keyboard accessible.
    ...(needsTabIndex(children) && { tabIndex: 0 }),
    ...rest
  });

  // Tooltips can be used in a dark-themed component, so we need to explicitly set
  // the light theme class in order to enable having a different theme than the
  // parent.
  const style = getThemeStyle(darkTheme ? "dark" : "light");
  const content =
    typeof tip === "string" ? (
      <VuiText size="xs">
        <p>
          <VuiTextColor color="empty">{tip}</VuiTextColor>
        </p>
      </VuiText>
    ) : (
      tip
    );
  const tooltip = (
    <Tooltip id={tooltipId} offset={10} className="vuiTooltip" style={style} opacity={1} place={position}>
      {content}
    </Tooltip>
  );

  return (
    <>
      {target}
      {usePortal ? <VuiPortal>{tooltip}</VuiPortal> : tooltip}
    </>
  );
};

// This is a workaround for the issue with ResizeObserver in ReactTooltip.
// Without this, uncaught runtime errors are thrown: "ResizeObserver loop
// completed with undelivered notifications."
// See https://github.com/ReactTooltip/react-tooltip/issues/1104
const debounce = (callback: (...args: any[]) => void, delay: number) => {
  let tid: any;
  return function (...args: any[]) {
    // eslint-disable-next-line no-restricted-globals
    const ctx = self;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = (window as any).ResizeObserver;
(window as any).ResizeObserver = class ResizeObserver extends _ {
  constructor(callback: (...args: any[]) => void) {
    callback = debounce(callback, 20);
    super(callback);
  }
};
